/**
 * API Client
 * Fetch wrapper with error handling and authentication
 */

import { env } from '@/utils/env';
import type { ApiError } from '@/types/errors';
import { AppError, AuthError, NetworkError } from '@/types/errors';
import { useAuthStore } from '@/stores/authStore';
import i18n from '@/locales';

/**
 * Get the current language for API requests
 * Returns "de" or "en" based on current i18n language
 */
function getCurrentLanguage(): 'deutsch' | 'english' {
  const lang = i18n.language?.split('-')[0];
  return lang === 'de' ? 'deutsch' : 'english';
}

/**
 * Inject language parameter into request body
 */
function injectLanguage(body: unknown): unknown {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    return { ...body, language: getCurrentLanguage() };
  }
  if (body === undefined || body === null) {
    return { language: getCurrentLanguage() };
  }
  return body;
}

/**
 * HTTP methods supported by the client
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Request options for the API client
 */
interface RequestOptions {
  /** Request body (will be JSON stringified) */
  body?: unknown;
  /** Additional headers */
  headers?: Record<string, string>;
  /** Request timeout in milliseconds */
  timeout?: number;
}

/**
 * Get the stored access token
 */
function getAccessToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

/**
 * Build headers for API requests
 */
function buildHeaders(customHeaders?: Record<string, string>): Headers {
  const headers = new Headers({
    'Content-Type': 'application/json',
    ...customHeaders,
  });

  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

/**
 * Create a timeout promise
 */
function createTimeout(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new NetworkError('NETWORK_TIMEOUT', `Request timed out after ${ms}ms`));
    }, ms);
  });
}

/**
 * User-friendly error messages for HTTP status codes
 */
const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'The email or password you entered is incorrect.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You don\'t have permission to access this resource.',
  404: 'The requested resource was not found.',
  409: 'This action conflicts with existing data. Please refresh and try again.',
  422: 'The provided data is invalid. Please check your input.',
  429: 'Too many attempts. Please wait a moment and try again.',
  500: 'Something went wrong on our end. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
  504: 'The request timed out. Please try again.',
};

/**
 * Get user-friendly message for HTTP status code
 */
function getHttpErrorMessage(status: number): string {
  return HTTP_ERROR_MESSAGES[status] || 'An unexpected error occurred. Please try again.';
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<ApiError> {
  try {
    const data = await response.json();
    if (data.code && data.message) {
      return data as ApiError;
    }
    // Backend may return error message in 'error' or 'message' field
    const errorMessage = data.message || data.error;
    return {
      code: `HTTP_${response.status}`,
      message: errorMessage || getHttpErrorMessage(response.status),
    };
  } catch {
    return {
      code: `HTTP_${response.status}`,
      message: getHttpErrorMessage(response.status),
    };
  }
}

/**
 * Make an API request
 */
async function request<T>(
  method: HttpMethod,
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, headers: customHeaders, timeout = 30000 } = options;

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${env.apiUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const fetchOptions: RequestInit = {
    method,
    headers: buildHeaders(customHeaders),
  };

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(injectLanguage(body));
  }

  try {
    const response = await Promise.race([
      fetch(url, fetchOptions),
      createTimeout(timeout),
    ]);

    if (!response.ok) {
      // Handle 401 Unauthorized - but skip session expiration handling for auth endpoints
      // (login returns 401 for invalid credentials, not expired sessions)
      const isAuthEndpoint = endpoint.includes('/Auth/login') || endpoint.includes('/Auth/magic-link');

      if (response.status === 401 && !isAuthEndpoint) {
        useAuthStore.getState().logout();
        window.location.href = '/login?sessionExpired=true';
        throw new AuthError('AUTH_SESSION_EXPIRED', 'Your session has expired. Please sign in again.');
      }

      const apiError = await parseErrorResponse(response);
      throw AppError.fromApiError(apiError);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return await response.json();
  } catch (error) {
    if (error instanceof AppError) {
      throw error;
    }
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new NetworkError('NETWORK_ERROR', 'Unable to connect to the server');
    }
    throw error;
  }
}

/**
 * API client with methods for all HTTP verbs
 */
export const apiClient = {
  /**
   * Make a GET request
   */
  get<T>(endpoint: string, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return request<T>('GET', endpoint, options);
  },

  /**
   * Make a POST request
   */
  post<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return request<T>('POST', endpoint, { ...options, body });
  },

  /**
   * Make a PUT request
   */
  put<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return request<T>('PUT', endpoint, { ...options, body });
  },

  /**
   * Make a PATCH request
   */
  patch<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return request<T>('PATCH', endpoint, { ...options, body });
  },

  /**
   * Make a DELETE request
   */
  delete<T>(endpoint: string, options?: Omit<RequestOptions, 'body'>): Promise<T> {
    return request<T>('DELETE', endpoint, options);
  },
};

/**
 * Export request function for advanced use cases
 */
export { request };
